import {Component} from "react";
import PropTypes from "prop-types";
import Glob from "../../libs/Glob.mjs";
import './FolderUploader.css';
import TextLabel from "../TextLabel/TextLabel.mjs";
import {Button, ButtonGroup, Input} from "reactstrap";

const ComponentFolderIcon = class extends Component {
	render() {
		return (
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
				<path
					d="M26,14a7.95668,7.95668,0,0,0,2-.26331V26a2.00006,2.00006,0,0,1-2,2H6a2.00006,2.00006,0,0,1-2-2V12H20.72626A7.95755,7.95755,0,0,0,26,14ZM18.06946,7H13L11.58582,5.58582A1.99993,1.99993,0,0,0,10.17157,5H6A2.00006,2.00006,0,0,0,4,7v3H19.08246A7.93128,7.93128,0,0,1,18.06946,7ZM32,6a6,6,0,1,1-6-6A6,6,0,0,1,32,6Zm-3-.5a.5.5,0,0,0-.5-.5H27V3.5a.5.5,0,0,0-.5-.5h-1a.5.5,0,0,0-.5.5V5H23.5a.5.5,0,0,0-.5.5v1a.5.5,0,0,0,.5.5H25V8.5a.5.5,0,0,0,.5.5h1a.5.5,0,0,0,.5-.5V7h1.5a.5.5,0,0,0,.5-.5Z"/>
			</svg>
		)
	}
}

const formatSize = size => {
	if (!size || typeof size !== "number") return "—";
	const units = ['B', 'KB', 'MB', 'GB'];
	let i = 0;
	while (size > 1000 && i < 4) {
		size /= 1000;
		i++;
	}
	size = parseFloat((Math.round(size * 100) / 100).toFixed(2));
	return `${size.toLocaleString(Glob.getLanguageLong())} ${units[i]}`;
}

/** @type {FolderUploaderHelperClass} */
const MultipleUploaderHelper = class {
	/** @type {Record<string,any>} */
	#props = {};

	/**
	 * @function
	 * @private
	 * @param {MultipleUploaderInit} init
	 */
	constructor(init) {
		/** @type {boolean | MultipleUploaderInit} */
		this.#props.initialized = false;
		/** @type {Array<Object>} */
		this.#props.fileList = [];
		/** @type {FileTreeNode} */
		this.#props.fileTree = {};
		/** @type {number} */
		this.#props.numFolders = 0;
		/** @type {number} */
		this.#props.totalSize = 0;
		/** @type {Array<string>} */
		this.#props.accept = ['.pjson', '.json', 'application/json'];
		this.#setup(init);
	}

	#setup(init) {
		if (this.#props.initialized) return;
		if (!init.folderInputId) init.folderInputId = init.baseID + '-folder-input';
		if (!init.dropZoneId) init.dropZoneId = init.baseID + '-drop-zone';
		if (!init.dropZoneEventListeners) init.dropZoneEventListeners = {};
		if (!init.dropZoneEventListeners.click) init.dropZoneEventListeners.click = () => this.folderInput.click();
		if (!init.dropZoneEventListeners.dragover) init.dropZoneEventListeners.dragover = () => this.getDropZone().classList.add('drag-over');
		if (!init.dropZoneEventListeners.dragLeave) init.dropZoneEventListeners.dragLeave = () => this.getDropZone().classList.remove('drag-over');
		if (!init.dropZoneEventListeners.mouseleave) init.dropZoneEventListeners.mouseleave = () => this.getDropZone().classList.remove('drag-over');
		if (!init.dropZoneEventListeners.drop) init.dropZoneEventListeners.drop = false;
		if (Array.isArray(init.accept)) {
			this.#props.accept = init.accept;
		} else if (init.accept === false) {
			this.#props.accept = [];
		} else if (typeof init.accept === "function") {
			this.#props.accept = [];
			init.filter = init.accept;
		}
		this.#props.initialized = init;
		this.#props.accept = this.#props.accept.map(a => {
			a = a.toLowerCase();
			if (!a.includes('/') && !a.startsWith('.')) a = '.' + a;
			return a;
		});
		this.setFolderInput();
		this.setDropZone(init.dropZoneEventListeners);
	}

	fileList(list) {
		if (list && Array.isArray(list)) this.#props.fileList = list;
		return this.#props.fileList;
	}

	handleDrop(evt) {
		const dt = evt.dataTransfer;
		const items = dt.items;
		const list = [];
		let countAsyncInProgress = 0;
		const traverseFileTree = (entry, path) => {
			path = path || "";
			if (entry.isFile) {
				entry.file(file => {
					//console.log("File:", path + file.name);
					if (file.name.match(/^[.~!]/)) return;
					if (!this.accept(file)) return;
					file.filePath = path + file.name;
					if (!list.includes(file)) list.push(file);
					countAsyncInProgress++;
					this.addFileToTree(file).then(() => countAsyncInProgress--);

				});
			} else if (entry.isDirectory && !entry.name.startsWith('.')) {
				let dirReader = entry.createReader();
				countAsyncInProgress++;
				dirReader.readEntries((entries) => {
					for (let i = 0; i < entries.length; i++) {
						traverseFileTree(entries[i], path + entry.name + "/");
					}
					countAsyncInProgress--;
				});
			}
		}
		this.activateLoader();
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			// Use webkitGetAsEntry to check if it's a directory - ignore files!
			if (item.kind === 'file') {
				const entry = item.webkitGetAsEntry();
				if (entry.isDirectory) traverseFileTree(entry);
			}
		}
		this.getDropZone().classList.remove('drag-over');
		const ret = () => {
			if (countAsyncInProgress) {
				setTimeout(ret, 50);
			} else {
				//console.log(list);
				this.fileList(list);
				this.deactivateLoader();
			}
		}
		setTimeout(ret, 50);
		//this.fileList(list);
		//this.deactivateLoader();
	}

	fileTree(tree) {
		if (tree && typeof tree === 'object') this.#props.fileTree = tree;
		return this.#props.fileTree;
	}

	/**
	 * @function
	 * @private
	 * @param {FileSystemFileEntry} file
	 * @return {boolean}
	 */
	accept(file) {
		if (this.#props.accept.length) {
			if (this.#props.accept.includes('*')) return true;
			if (file?.type && file.type.includes('/')) {
				let t = file.type.toLowerCase();
				if (this.#props.accept.includes(t)) return true;
				t = t.split('/');
				if (this.#props.accept.includes('*/' + t.pop())) return true;
				if (this.#props.accept.includes(t.shift() + '/*')) return true;
			}
			if (file?.name && file.name.includes('.')) {
				if (this.#props.accept.includes('.' + file.name.split('.').pop().toLowerCase())) return true;
			}
		}
		if (typeof this.#props.initialized.filter === 'function') return this.#props.initialized.filter(file);
		return false;
	}

	numFolders(num) {
		if (num) this.#props.numFolders = num;
		return this.#props.numFolders;
	}

	totalSize(s) {
		if (s) this.#props.totalSize = s;
		return this.#props.totalSize;
	}

	getDropZone() {
		if (!this.dropZone) this.dropZone = window.document.getElementById(this.#props.initialized.dropZoneId);
		return this.dropZone;
	}

	getFolderInput() {
		if (!this.folderInput) this.folderInput = window.document.getElementById(this.#props.initialized.folderInputId);
		return this.folderInput;
	}

	/**
	 * @function
	 * @private
	 * finds file input tag by id and sets it, if id is not provided, 'folder-input' is the default
	 * this functions also sets event listeners to the drop zone
	 */
	setFolderInput = () => {
		const fi = this.getFolderInput();
		fi.addEventListener('change', (e) => {
			e.preventDefault();
			e.stopPropagation();
			this.activateLoader();
			if (fi.files.length > 0) {
				this.processFileList(fi.files).then(this.deactivateLoader);
			} else {
				this.deactivateLoader();
			}
		});
		this.folderInput = fi;
	}

	/**
	 * @function
	 * @private
	 * @param {object | undefined} eventListeners
	 * finds drop zone by id and sets it, if id is not provided, 'drop-zone' is the default
	 * this functions also sets event listeners to the drop zone
	 */
	setDropZone = eventListeners => {
		if (!this.folderInput) this.setFolderInput();
		const dp = this.getDropZone();
		// All D&D handlers must prevent default
		Object.entries(eventListeners).forEach(([eventName, eventHandler]) => {
			if (eventName === 'click') {
				dp.addEventListener(eventName, eventHandler, false);
			} else if (eventName === 'drop') {
				dp.addEventListener(eventName, evt => {
					evt.preventDefault();
					evt.stopPropagation();
					this.handleDrop(evt);
					if (typeof eventHandler === 'function') eventHandler(evt);
				}, false);
			} else {
				dp.addEventListener(eventName, evt => {
					evt.preventDefault();
					evt.stopPropagation();
					eventHandler(evt);
				}, false);
			}
		});
		//this.dropZone = dp;
	}
	/**
	 * @function
	 * @private
	 * @param {Array<File>} files
	 */
	processFileList = async (files) => {
		for (let file of files) {
			if (file.name.match(/^[.~!]/)) continue;
			if (!this.accept(file)) continue;
			if (this.fileList().some(f => f.name === file.name)) continue;
			this.fileList([file, ...this.fileList()]);
			await this.addFileToTree(file);
		}
		//console.log(this.fileList());
		//console.debug(this.fileTree());
	}

	readFile = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new window.FileReader();
			reader.onload = () => {
				try {
					file.json = JSON.parse(reader.result);
				} catch (e) {
					file.text = reader.result;
				}
				resolve(file);
			};
			try {
				reader.readAsText(file);
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * @function
	 * @private
	 * @param {File} file
	 */
	addFileToTree = async (file) => {
		if (!file.filePath) file.filePath = file.webkitRelativePath;
		const folders = file.filePath.split('/');

		//set root folder
		if (!this.fileTree().name) {
			this.fileTree({parent: null, name: folders[0], subfolders: {}, files: []});
			this.numFolders(1);
		}
		let node = this.fileTree();
		for (let i = 1; i < folders.length; i++) {
			if (i === folders.length - 1) {
				//node.files.push(folders[i]);
				await this.readFile(file);
				// let content = await this.readFile(file);
				node.files.push(file);
				this.totalSize(this.totalSize() + file.size);
				break;
			}
			if (!Object.keys(node.subfolders).includes(folders[i])) {
				node.subfolders[folders[i]] = {name: folders[i], parent: node.name, subfolders: {}, files: []};
				this.numFolders(this.numFolders() + 1)
			}
			node = node.subfolders[folders[i]];
		}
	}

	activateLoader = () => {
		if (typeof this.#props.initialized?.beforeChange === 'function') {
			this.#props.initialized?.beforeChange();
		}
	}

	deactivateLoader = () => {
		//console.log(this.fileList());
		if (typeof this.#props.initialized?.afterChange === 'function') {
			this.#props.initialized?.afterChange(this.fileList());
		}
	}
}

const prefix = 'FolderUploader';

/**
 * @desc This component can be used to upload one or more folders
 * @abstract
 * A convenient tool to click (or D&D) one or more folders and collect the related files.
 * @abstract
 * In case of JSON files the content is also automatically parsed.
 * @version 1.0
 * @author Marco Di Tullo
 * @author [Marco Balestra](https://altersoftware.it)
 * @type {Component}
 * @property {string|false} [title] Component title, false doesn't display title
 * @property {string|false} [info] Component info comment, false doesn't display info
 * @property {boolean} [single] Only accept one single folder
 * @property {Array<string>|false|Function} [accept] An array of strings (types/extensions), or false (for all)
 * @property {Function} [filter] File filter: receives a single file, returns boolean for inclusion
 * @property {Function} [valid] Validation function: receives the list of files, returns boolean for global validity
 * @property {Function} [onChange] Invoked onChange, receives the list of files
 * @property {Function} [save] If provided, the "Save" button will be rendered and used
 * @property {string} [saveButton] Optional value for Save button
 * @property {string} [lang] Override global language settings with this two-characters code
 */
const FolderUploader = class extends Component {
	static propTypes = {
		/** Component title, false doesn't display title */
		title: PropTypes.oneOf([PropTypes.string, false]),
		/** Component info comment, false doesn't display info */
		info: PropTypes.string,
		/** If true only one single folder (and its content) is accepted */
		single: PropTypes.bool,
		/**
		 * accept can be:
		 * * An array of strings (types/extensions)
		 * * false
		 * * A function (MultipleUploaderFile)=>boolean
		 * @desc In case of type/extension array consider that:
		 * * Both type and extensions are case-insensitive
		 * * Extensions can start with a "." or omit it ("json" or ".json")
		 * * MIME wildcards are allowed, e.g.: "*\/json" or "text\/*"
		 */
		accept: PropTypes.oneOf([
			PropTypes.arrayOf(PropTypes.string),
			PropTypes.func,
			false
		]),
		/**
		 * Filter function
		 * @function
		 * @param {MultipleUploaderFile} file
		 * @return {boolean}
		 */
		filter: PropTypes.func,
		/**
		 * Validation function
		 * @function
		 * @param {MultipleUploaderFiles} files
		 * @return {boolean}
		 */
		valid: PropTypes.func,
		/**
		 * onChange function
		 * @function
		 * @param {MultipleUploaderFiles} files
		 * @return {void}
		 */
		onChange: PropTypes.func,
		/** Short-form (two characters) language code, defaults to Glob.getLanguage() */
		lang: PropTypes.string,
		/**
		 * Function to be invoked on "save" action
		 * If this function is missing, no "save" button will be produced
		 * @param {MultipleUploaderFiles} files Chosen files
		 * @return {void}
		 */
		save: PropTypes.func,
		/** Labe of save button, if any */
		saveButton: PropTypes.string,
	}

	constructor(props) {
		super(props);
		this.state = {
			lang: this.props.lang ?? Glob.getLanguage(),
			initializedOn: (new Date()).getTime()
		};
		this.uid = Glob.generateUid(prefix);
		this.renderFiles = this.renderFiles.bind(this);
		this.init = this.init.bind(this);
		this.afterInit = this.afterInit.bind(this);
		this.handleReset = this.handleReset.bind(this);
		setTimeout(this.init);
	}

	handleReset() {
		this.setState({
			helper: false,
			initializedOn: (new Date()).getTime(),
			hasFiles: false,
			hasError: typeof this.props.valid === 'function' ? !this.props.valid([]) : false
		}, () => {
			this.init();
			if (typeof this.props.onChange === 'function') setTimeout(() => {
				this.props.onChange([])
			});
		});
	}

	componentDidMount() {
	}

	init() {
		if (this.state.helper || this.state.buildingHelper) return;
		if (!window.document.getElementById(this.uid + "-drop-zone")) {
			setTimeout(this.init, 100);
			return;
		}
		this.setState({buildingHelper: true}, () => {
			/** @type {FolderUploaderHelperObject} */
			const helper = new MultipleUploaderHelper({
				baseID: this.uid,
				accept: this.props.accept,
				filter: this.props.filter,
				beforeChange: () => {
					this.setState({loading: true, hasFiles: false});
				},
				afterChange: files => {
					const ns = {loading: false, hasFiles: files.length, hasError: false};
					if (typeof this.props.valid === 'function') ns.hasError = !this.props.valid(files);
					this.setState(ns, () => {
						if (typeof this.props.onChange === 'function') setTimeout(() => {
							this.props.onChange(files)
						});
					});
				}
			});
			this.setState({buildingHelper: false, helper: helper}, this.afterInit);
		});
	}

	afterInit() {
		//console.log( this.state.helper );
	}

	componentWillUnmount() {
	}

	renderFile(p) {
		const parts = p.filePath.split('/');
		parts.shift();
		return parts.map((s, idx) => {
			const out = [];
			if (idx) out.push(<span key={idx - .5} className={"sep"}>/</span>);
			out.push(<span key={idx}>{s}</span>);
			return out;
		}).concat(<tt key={"size"}>{formatSize(p.size)}</tt>);
	}

	renderFiles() {
		if (this.props.renderFiles === false) return null;
		const files = this.state.helper.fileList().sort((a, b) => {
			return a.filePath.localeCompare(b.filePath);
		});
		return files.map((file, idx) => (
			<div key={idx} className={"tree-item"}>
				{typeof this.props.renderFiles === 'function' ? this.props.renderFiles(file, idx) : this.renderFile(file)}
			</div>
		));
	}

	render() {
		return (
			<div className={prefix} id={this.uid} key={this.state.initializedOn}>
				{this.props.title !== false && (
					<h1>
						<TextLabel value={this.props.title ?? 'Folder loader'}/>
					</h1>
				)}
				<div
					className={"drop-zone"}
					id={this.uid + "-drop-zone"}
					hidden={!!(this.state.hasFiles && this.props.single)}
				>
					<ComponentFolderIcon/>
					{this.props.info !== false && (
						<p>
							<TextLabel value={this.props.info ?? 'Drop a folder here, or click to choose'}/>
						</p>
					)}
					<Input
						type="file"
						className={"folder-input"}
						id={this.uid + "-folder-input"}
						webkitdirectory={"true"}
						multiple
					/>
					{!!this.state.loading && <div className={"loader"}>
						<div/>
					</div>}
				</div>
				{this.state.helper &&
					<>
						<div className={"stats"} hidden={!this.state.hasFiles}>
							<div className={"stat"}>
								<span className={"stat-value"}>
									{this.state.hasFiles}
								</span>
								<span className={"stat-label"}>
									<TextLabel value={"Files"}/>
								</span>
							</div>

							<div className={"stat"}>
								<span className={"stat-value"}>
									{this.state.helper.numFolders()}
								</span>
								<span className={"stat-label"}>
									<TextLabel value={"Folders"}/>
								</span>
							</div>

							<div className={"stat"}>
								<span className={"stat-value"}>
									{formatSize(this.state.helper.totalSize())}
								</span>
								<span className={"stat-label"}>
									<TextLabel value={"Total size"}/>
								</span>
							</div>
							{!this.props.save && (
								<Button
									className={"btn"}
									color="warning"
									onClick={() => this.handleReset()}
									disabled={!this.state.hasFiles}
								>
									<b>
										<TextLabel value={"Reset"}/>
									</b>
								</Button>
							)}
						</div>

						<div className={'tree-container'} hidden={!this.state.hasFiles}>
							<div className={"tree-content"}>
								{this.renderFiles()}
							</div>
						</div>

						{this.props.save && (
							<ButtonGroup>
								<Button
									className={"btn"}
									color="warning"
									onClick={() => this.handleReset()}
									disabled={!this.state.hasFiles}
								>
									<b>
										<TextLabel value={"Reset"}/>
									</b>
								</Button>
								<Button
									disabled={!this.state.hasFiles || this.state.hasError}
									color={"success"}
									onClick={async () => {
										await this.props.save(this.state.helper.fileTree());
										this.handleReset();
									}}
								>
									<b>
										<TextLabel value={this.props.saveButton ?? "Save"}/>
									</b>
								</Button>
							</ButtonGroup>
						)}
					</>
				}
			</div>
		);
	}
}

export default FolderUploader;
